import React from 'react'
import AntSwitch from '@components/AntSwitch'
import { Cancel, CheckCircle } from '@mui/icons-material'
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableRow,
  Typography,
} from '@mui/material'
import { useTranslation } from 'next-i18next'
import GetStartedButton from '@imports/GetStartedButton'

function FeatureIcon({ check }) {
  return check ? (
    <CheckCircle sx={{ color: 'success.dark', transform: 'scaleX(1)!important' }} />
  ) : (
    <Cancel sx={{ color: 'error.dark' }} />
  )
}
function RegularWide({ plans: allPlans, featurePlans: allFeaturePlans }) {
  const { t } = useTranslation()
  const [checked, setChecked] = React.useState(true)
  const plans = [...(allPlans ?? [])]
    ?.sort((a, b) => a.amount - b.amount)
    ?.filter((plan) =>
      checked ? plan.periodicity_type === 'Year' : plan.periodicity_type === 'Month'
    )
  const featurePlans = allFeaturePlans?.filter((plan) => plan.title !== 'Main')
  const mainSection = allFeaturePlans?.filter((plan) => plan.title === 'Main')?.[0]

  return (
    <TableContainer component={Paper}>
      <Table
        sx={{
          pt: 10,
          '& td:first-of-type': {
            pl: 6,
          },
          '& td:last-of-type': {
            pr: 6,
          },
        }}
      >
        <TableBody>
          <TableRow>
            <TableCell sx={{ border: 0 }} />
            {plans?.map((plan, index) => (
              <TableCell
                align="center"
                key={index}
                sx={{ border: 0 }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  {!!plan.special && (
                    <Paper
                      sx={{
                        py: 1,
                        // px: 4,
                        backgroundColor: 'secondary.main',
                        color: 'secondary.contrastText',
                        position: 'absolute',
                        top: -25,
                        zIndex: 2,
                        // left: 0,
                        // right: 0,
                        width: '125px',
                        margin: 'auto',
                      }}
                    >
                      <Typography
                        variant="button"
                        fontWeight={600}
                        fontSize={18}
                      >
                        {plan.special}
                      </Typography>
                    </Paper>
                  )}
                  <Typography textTransform="capitalize">{plan.name_text}</Typography>
                </Box>
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell sx={{ border: 0 }}>
              <Typography
                variant="body2"
                color="primary.main"
                fontWeight={700}
                gutterBottom
                display="block"
              >
                Billed {checked ? 'Annually' : 'Monthly'}
              </Typography>
              <AntSwitch
                inputProps={{ 'aria-label': 'Billing' }}
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
              />
            </TableCell>
            {plans.map((plan, index) => (
              <TableCell
                align="center"
                key={index}
                sx={{ border: 0 }}
              >
                <Typography
                  variant="h5"
                  fontWeight={700}
                >
                  {plan.amount}{' '}
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    component="span"
                  >
                    {plan.currency}/{plan.periodicity_type}
                  </Typography>
                </Typography>
              </TableCell>
            ))}
          </TableRow>
          {mainSection?.features?.map((feature) => (
            <TableRow key={feature.name}>
              <TableCell sx={{ border: 0 }}>
                <Typography
                  variant="body2"
                  fontWeight={700}
                >
                  {feature.name_text}
                </Typography>
              </TableCell>
              {plans.map((plan, index) => (
                <TableCell
                  align="center"
                  key={index}
                  sx={{ border: 0 }}
                >
                  {typeof feature.plans[plan.name] !== 'boolean' ? (
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color="primary.main"
                    >
                      {feature.plans[plan.name]}
                    </Typography>
                  ) : (
                    <FeatureIcon check={feature.plans[plan.name]} />
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}

          {featurePlans.map(({ title, features }, index) => (
            <React.Fragment key={index}>
              <TableRow sx={{ backgroundColor: '#F9F9F9' }}>
                <TableCell
                  sx={{ border: 0, py: 3 }}
                  colSpan={plans.length + 1}
                >
                  <Typography
                    variant="h6"
                    fontWeight={700}
                  >
                    {title}
                  </Typography>
                </TableCell>
              </TableRow>
              {features.map((feature, idx) => (
                <TableRow key={idx}>
                  <TableCell
                    sx={{
                      borderWidth: features.length > idx + 1 ? 1 : 0,
                      borderRightWidth: 1,
                      borderRight: '1px solid rgba(224, 224, 224, 1)',
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight={500}
                    >
                      {feature.name_text}
                    </Typography>
                  </TableCell>
                  {plans.map((plan, i) => (
                    <TableCell
                      align="center"
                      key={i}
                      sx={{
                        borderWidth: features.length > idx + 1 ? 1 : 0,
                        borderRight:
                          plans.length > i + 1 ? '1px solid rgba(224, 224, 224, 1)' : 'unset',
                      }}
                    >
                      {typeof feature.plans[plan.name] !== 'boolean' ? (
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color="primary.main"
                        >
                          {feature.plans[plan.name] >= 999999
                            ? t('pricing:Unlimited')
                            : feature.plans[plan.name]}
                        </Typography>
                      ) : (
                        <FeatureIcon check={feature.plans[plan.name]} />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell
              sx={{
                borderTop: '1px solid rgba(224, 224, 224, 1)',
              }}
            />
            {plans.map((_, i) => (
              <TableCell
                align="center"
                key={i}
                sx={{
                  borderTop: '1px solid rgba(224, 224, 224, 1)',
                }}
              >
                <GetStartedButton
                  sx={{ minWidth: 150, borderRadius: 10, fontSize: 16 }}
                  color="primary"
                  label={t('common:get_started')}
                />
              </TableCell>
            ))}
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  )
}

export default RegularWide
